import { inject } from '@angular/core';
import type { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { Store } from '@ngxs/store';
import { catchError, EMPTY, firstValueFrom } from 'rxjs';

import { collectRouteParams, collectRouteQueryParams } from '@cosmos/router';
import { PreviewService } from '@customer-portal/data-access-preview';
import {
  CustomerPortalDistributorQueries,
  CustomerPortalPresentationQueries,
} from '@customer-portal/presentations/data-access-presentations';
import {
  CustomerPortalDistributorActions,
  CustomerPortalPresentationActions,
} from '@customer-portal/presentations/data-access-presentations/actions';
import { CustomerPortalProjectDetailsActions } from '@customer-portal/projects/data-access/project-details/actions';
import { CustomerPortalProjectDetailsQueries } from '@customer-portal/projects/data-access/project-details/queries';
import {
  CustomerPortalProjectActions,
  CustomerPortalProjectQueries,
} from '@customer-portal/projects/data-access/store';

/**
 * New version of `resolveProjectDetails` resolver that handles cases when entities might be already loaded
 * in another resolver (e.g `resolvePresentation` resolver is a separate branch in the router tree)
 */
export const resolveProjectDetails: ResolveFn<void> = (
  route: ActivatedRouteSnapshot
) => {
  const store = inject(Store);
  const previewService = inject(PreviewService);
  const { projectId } = collectRouteParams(route.root);
  const { settings: settingsParam } = collectRouteQueryParams(route.root);
  const project = store.selectSnapshot(CustomerPortalProjectQueries.getProject);

  // in mocked entities mode we skip data loading
  if (
    previewService.useMockProjectAndPresentation &&
    project &&
    project.Id === parseInt(projectId, 10)
  ) {
    return;
  }

  // Note: do not return the result of this observable since it shouldn't
  // block the UI from loading; this should act as fire & forget; hereby,
  // we're able to show skeleton loaders.

  // At this point we might already have all data loaded so we check it and load entities only if it's needed.

  // Check if project is already loaded; if not, fire loading (fire-and-forget).
  if (!project || project.Id !== parseInt(projectId, 10)) {
    store.dispatch(new CustomerPortalProjectActions.GetProject(projectId));
  }

  const projectPresentationIdPromise = (async function () {
    const presentationIdSignal = store.selectSignal(
      CustomerPortalProjectDetailsQueries.getPresentationId
    );
    if (presentationIdSignal()) {
      return presentationIdSignal();
    }
    // otherwise lets load project details to get presentation id
    await firstValueFrom(
      store
        .dispatch(
          new CustomerPortalProjectDetailsActions.GetProjectDetails(projectId)
        )
        .pipe(catchError(() => EMPTY)),
      { defaultValue: undefined }
    );
    return presentationIdSignal();
  })();

  projectPresentationIdPromise.then((presentationId) => {
    if (!presentationId) {
      return;
    }
    const actions: Array<object> = [];
    // Check if distributor is already loaded; if not, add it to the loading batch.
    if (
      !store.selectSnapshot(CustomerPortalDistributorQueries.getDistributor)
    ) {
      actions.push(
        new CustomerPortalDistributorActions.GetDistributor(presentationId)
      );
    }
    // Check if design is already loaded; if not, add it to the loading batch.
    if (!store.selectSnapshot(CustomerPortalDistributorQueries.getDesign)) {
      store.dispatch([
        new CustomerPortalDistributorActions.GetDesign(presentationId),
      ]);
    }
    const presentation = store.selectSnapshot(
      CustomerPortalPresentationQueries.getPresentation
    );
    // Check if presentation is already loaded; if not, add it to the loading batch.
    if (presentation?.Id !== presentationId) {
      actions.push(
        new CustomerPortalPresentationActions.GetPresentation({
          presentationId,
          shouldLoadQuote: true,
          shouldLoadTemplate: true,
          settings: settingsParam,
        })
      );
    } else {
      if (settingsParam) {
        actions.push(
          new CustomerPortalPresentationActions.ApplyPresentationSettingsParams(
            settingsParam
          )
        );
      }
    }
    // Dispatch all actions in batch.
    store.dispatch(actions);
  });
};
